import React, { useRef, useState } from 'react';
import type { MusicGenerationPrefs, ParsedScoreAst } from '@interactive-room';
import { parseScoreUpload } from '../api/scoreApi';

interface ScoreUploadPanelProps {
  value: ParsedScoreAst | null;
  onChange: (ast: ParsedScoreAst | null) => void;
  musicPrefs?: MusicGenerationPrefs;
}

export function ScoreUploadPanel({ value, onChange, musicPrefs }: ScoreUploadPanelProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File | null) => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const ast = await parseScoreUpload(file, undefined, musicPrefs);
      onChange(ast);
    } catch (e) {
      onChange(null);
      setError(e instanceof Error ? e.message : 'No se pudo leer el archivo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-lg border border-[var(--sala-border)] bg-[var(--sala-surface-2)] p-4">
      <div className="mb-2 flex items-start justify-between gap-3">
        <div>
          <p className="sala-label">Repertorio del curso (.mid / .musicxml)</p>
          <p className="text-[10px] text-[var(--sala-ink-dim)]">
            La IA aplicará ejercicios sobre el ritmo y melodía reales — no inventará figuras.
          </p>
        </div>
        {value && (
          <button
            type="button"
            className="text-[10px] text-slate-400 hover:text-red-400"
            onClick={() => onChange(null)}
          >
            Quitar
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".mid,.midi,.xml,.musicxml,.mxl"
        className="hidden"
        onChange={(e) => void handleFile(e.target.files?.[0] ?? null)}
      />

      {!value ? (
        <button
          type="button"
          className="studio-rail-btn w-full text-xs"
          disabled={loading}
          onClick={() => inputRef.current?.click()}
        >
          {loading ? 'Analizando partitura…' : 'Subir MIDI o MusicXML'}
        </button>
      ) : (
        <div className="space-y-1 text-xs text-[var(--sala-ink-muted)]">
          <p>
            <strong>{value.title}</strong> · {value.timeSignature} · {value.bpm} BPM
            {value.summary ? ` · ${value.summary.totalBars} compases` : ''}
          </p>
          <p className="font-mono text-[10px]">
            Compás 1: [{value.primaryPattern.join(', ')}]
          </p>
          {value.summary && (
            <div className="mt-2 space-y-0.5 rounded border border-[var(--sala-border)] bg-[var(--sala-surface)] p-2 text-[10px]">
              <p>
                <span className="text-[var(--sala-ink-dim)]">Tonalidad:</span>{' '}
                {value.summary.keySignature} · Clave {value.summary.clef === 'G' ? 'Sol' : 'Fa'}
              </p>
              <p>
                <span className="text-[var(--sala-ink-dim)]">Rango:</span>{' '}
                {value.summary.metrics.melodicRange.min} – {value.summary.metrics.melodicRange.max}
              </p>
              <p>
                <span className="text-[var(--sala-ink-dim)]">Compás más complejo:</span>{' '}
                #{value.summary.metrics.hardestBarNumber} · figura dominante:{' '}
                {value.summary.metrics.mostUsedFigure}
              </p>
              {value.summary.metrics.musicalSymbolsPresent.length > 0 && (
                <p>
                  <span className="text-[var(--sala-ink-dim)]">Símbolos:</span>{' '}
                  {value.summary.metrics.musicalSymbolsPresent.join(', ')}
                </p>
              )}
            </div>
          )}
          {value.melodyNotes.length > 0 && (
            <p className="text-[10px]">
              Melodía: {value.melodyNotes.slice(0, 6).map((n) => n.label).join(' · ')}
              {value.melodyNotes.length > 6 ? '…' : ''}
            </p>
          )}
        </div>
      )}

      {error && <p className="mt-2 text-[10px] text-red-400">{error}</p>}
    </div>
  );
}
